
import type { PivotedQueriesDataItem } from '@empathybroker/tsclient-stats/dist/models/pivoted-queries/pivoted-queries-data-item'
import type { PivotedSearchDistributionData } from '@empathybroker/tsclient-stats/dist/models/pivoted-search/pivoted-search-distribution-data'
import type { PropType } from 'vue'
import type { KpisOptionComposable } from '../../../../composables'
import type { Filter, RegularFilter } from '../../../../models'

import { defineComponent, getCurrentInstance } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import { useFilters } from '../../../../composables'

import { DeviceEnum, FeatureIdEnum, FilterIdEnum, FilterTypeEnum, KpiEnum } from '../../../../enums'
import { capitalizeSentence } from '../../../../filters'

import { ANALYTICS_CONSTS } from '../../../../store/analytics/analytics.consts'
import { CONFIG_CONSTS } from '../../../../store/config/config.consts'

import KpisLegend from '../../../kpis-legend.component.vue'

import { EpbSwitch } from '../../../shared/epb-switch'
import Tabs from '../../../tabs.component.vue'
import PivotsChart from './pivots-chart.component.vue'
import PivotsTable from './pivots-table.component.vue'

export default defineComponent({
  name: 'Pivots',
  components: {
    KpisLegend,
    PivotsChart,
    PivotsTable,
    Tabs,
    EpbSwitch,
  },
  props: {
    kpiOptions: {
      type: Array as PropType<Array<KpisOptionComposable>>,
      required: true,
    },
    showRadarChart: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { availableFiltersFilteredByTool, isFilterVisible, filters } = useFilters()
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    return {
      availableFiltersFilteredByTool,
      isFilterVisible,
      filters,
      ctx,
    }
  },

  data() {
    return {
      pivotId: undefined,
      pivotIdWatcher: undefined,
      selectedKpi: undefined,
      messages: this.$epb.messages.insights.analytics,
      loading: false,
    }
  },
  computed: {
    ...mapGetters(ANALYTICS_CONSTS.moduleName, {
      data: ANALYTICS_CONSTS.getters.data,
      selectedId: ANALYTICS_CONSTS.getters.selectedId,
    }),
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.searchKpis,
    }),
    pivotData(): any[] {
      let statePivotData
      if (!this.selectedId) {
        statePivotData = this.data.overall.pivots[this.pivotId]
      } else if (this.data.ids[this.selectedId]) {
        statePivotData = this.data.ids[this.selectedId].pivots[this.pivotId]
      }
      if (!statePivotData) {
        return []
      }
      let pivotData: Array<{ id: string; currentPeriod: Record<string, number> }>
      function getItemId(item: PivotedSearchDistributionData, index: number): string {
        return item.pivots[0]?.value ?? `undefined-${index}`
      }
      if (!this.selectedId) {
        pivotData = statePivotData.currentPeriod.data.distributionData.map(
          (pivotDataItem: PivotedSearchDistributionData, index: number) => ({
            id: getItemId(pivotDataItem, index),
          }),
        )
      } else {
        pivotData = statePivotData.currentPeriod.data.items.map(
          (pivotDataItem: PivotedQueriesDataItem) => ({
            id: Object.values(pivotDataItem.pivots)[0],
          }),
        )
      }

      Object.keys(statePivotData).forEach((periodId: string) => {
        const periodData = statePivotData[periodId].data
        if (!this.selectedId) {
          periodData.distributionData.forEach((pivotDataItem: PivotedSearchDistributionData) => {
            const pivotValueData = pivotData.find(
              (valueData, index) => valueData.id === getItemId(pivotDataItem, index),
            )
            if (pivotValueData) {
              pivotValueData[periodId] = pivotDataItem.distributionDataPivots
            }
          })
        } else {
          periodData.items.forEach((pivotDataItem: PivotedQueriesDataItem) => {
            const pivotValueData = pivotData.find(
              valueData => valueData.id === Object.values(pivotDataItem.pivots)[0],
            )
            if (pivotValueData) {
              pivotValueData[periodId] = {
                queryCount: pivotDataItem.queryCount,
                clickCount: pivotDataItem.clickCount,
                findabilityCount: pivotDataItem.successCount,
                wishlistCount: pivotDataItem.wishlistCount,
                add2cartCount: pivotDataItem.add2cartCount,
                checkoutCount: pivotDataItem.checkoutCount,
                noResultsCount: pivotDataItem.queryNoResultsCount,
              }
            }
          })
        }
      })

      pivotData = this.getPivotVolumes(pivotData)
      pivotData.sort(
        (pivotDataItem1: any, pivotDataItem2: any) =>
          pivotDataItem2.currentPeriod.queryCount - pivotDataItem1.currentPeriod.queryCount,
      )
      return pivotData
    },
    pivots() {
      return this.ctx.proxy.$route.meta.featureId === FeatureIdEnum.EXPLAIN
        ? this.filters
        : this.availableFiltersFilteredByTool
    },
    tabsOptions() {
      return Object.values(this.pivots).reduce((tabsOptions: any[], filter: RegularFilter) => {
        if (
          filter.config.type === FilterTypeEnum.REGULAR &&
          filter.config.id !== FilterIdEnum.COMPARE &&
          this.isFilterVisible(filter.config)
        ) {
          tabsOptions.push({
            id: filter.config.taggingId || filter.config.id,
            text:
              filter.config.prettyName ||
              this.$epb.messages.filters.names[filter.config.id] ||
              filter.config.id,
          })
        }
        return tabsOptions
      }, [])
    },

    compareFilterValue(): string {
      let compareFilterValue: string
      const compareFilter = Object.values(this.pivots).find(
        (filter: Filter) => filter.config.id === FilterIdEnum.COMPARE,
      ) as RegularFilter
      if (compareFilter) {
        compareFilterValue = compareFilter.selectedValues[0]
      }
      return compareFilterValue
    },
    selectedKpiText(): string {
      const selectedKpiOptionText: string = this.kpiOptions.find(
        option => option.id === this.selectedKpi,
      ).label
      return capitalizeSentence(selectedKpiOptionText.toLowerCase())
    },
    selectedTab(): RegularFilter {
      return this.tabsOptions.find(regularFilter => regularFilter.id === this.pivotId)
    },
    selectedPivotKpiKey(): string {
      return {
        [KpiEnum.QUERIES]: () => 'queryCount',
        [KpiEnum.NO_RESULTS]: () => 'noResultsCount',
        [KpiEnum.CLICKS]: () => 'clickCount',
        [KpiEnum.FINDABILITY]: () => 'findabilityCount',
        [KpiEnum.ADD2CART]: () => 'add2cartCount',
        [KpiEnum.CHECKOUT]: () => 'checkoutCount',
      }[this.selectedKpi]()
    },
    pivotsComponentProps() {
      return {
        kpi: this.selectedKpi,
        compareFilterValue: this.compareFilterValue,
        pivot: this.selectedTab,
      }
    },
    isMobileDevice(): boolean {
      return this.$epb.device === DeviceEnum.MOBILE
    },
  },
  watch: {
    tabsOptions: {
      handler(): void {
        this.pivotId = this.tabsOptions?.[0]?.id
      },
      immediate: true,
    },
  },
  created() {
    this.selectedKpi = this.kpiOptions[0].id
    this.$watch(() => [this.pivotId, this.selectedId], this.loadData, { immediate: true })
  },
  methods: {
    ...mapActions(ANALYTICS_CONSTS.moduleName, {
      loadPivotData: ANALYTICS_CONSTS.actions.loadPivotData,
    }),
    async loadData(): Promise<void> {
      if (this.pivotId) {
        this.loading = true
        try {
          await this.loadPivotData({ pivotId: this.pivotId, selectedId: this.selectedId })
        } catch (error) {
          this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
        } finally {
          this.loading = false
        }
      }
    },
    getPivotVolumes(pivotData: Array<{ id: string; currentPeriod: Record<string, number> }>) {
      const pivotDataVolumes: Array<{ id: string; currentPeriod: Record<string, number> }> = [
        ...pivotData,
      ]
      const totalCurrentCount = pivotDataVolumes.reduce(
        (accum: number, pivot) => accum + pivot.currentPeriod[this.selectedPivotKpiKey],
        0,
      )
      const totalPreviousCount = this.compareFilterValue
        ? pivotDataVolumes.reduce((accum: number, pivot) => {
            const value: number = pivot[this.compareFilterValue]
              ? pivot[this.compareFilterValue][this.selectedPivotKpiKey]
              : 0
            return accum + value
          }, 0)
        : 0
      pivotDataVolumes.forEach((pivotDataItem: any) => {
        pivotDataItem.currentVolume =
          Math.round(
            (pivotDataItem.currentPeriod[this.selectedPivotKpiKey] / totalCurrentCount) * 1000,
          ) / 10
        if (pivotDataItem[this.compareFilterValue]) {
          pivotDataItem.previousVolume =
            Math.round(
              (pivotDataItem[this.compareFilterValue][this.selectedPivotKpiKey] /
                totalPreviousCount) *
                1000,
            ) / 10
        }
      })
      return pivotDataVolumes
    },
    onKpiChange(selectedKpis: KpiEnum[]): void {
      this.selectedKpi = selectedKpis[0]
    },
  },
})
